import React, { useCallback } from 'react';
import clsx from 'clsx';
import { LocationPicker } from '../tag/LocationPicker';
import { DateTimePicker } from '../tag/DateTimePicker';
import {
  SIGHTING_TYPE_OPTIONS,
  getLocalizedSightingTypeLabel,
  type SightingType,
} from '../../types/post';
import { useLanguage } from '../../i18n/useLanguage';

export interface SearchFiltersValue {
  sightingType?: SightingType;
  location?: {
    lat?: number;
    lon?: number;
    radiusKm?: number;
    locationName?: string;
  };
  dateRange?: {
    fromDate?: string;
    toDate?: string;
  };
}

export interface SearchFiltersProps {
  value: SearchFiltersValue;
  onChange: (next: SearchFiltersValue) => void;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}

const DEFAULT_RADIUS_KM = 10;
const MIN_RADIUS_KM = 1;
const MAX_RADIUS_KM = 1000;

/**
 * Collapsible filter panel rendered underneath the SearchBar on the home page.
 * Exposes the three filters the home feed actually uses: sighting type
 * (Steckbriefstyp), a location radius, and a date range.
 */
export const SearchFilters: React.FC<SearchFiltersProps> = ({
  value,
  onChange,
  expanded,
  onToggle,
  className,
}) => {
  const { language, t } = useLanguage();
  const sightingLanguage = language === 'de' ? 'de' : 'en';

  const sightingType = value.sightingType;
  const location = value.location ?? {};
  const dateRange = value.dateRange ?? {};

  const hasActiveFilters =
    !!sightingType ||
    (location.lat !== undefined && location.lon !== undefined) ||
    !!dateRange.fromDate ||
    !!dateRange.toDate;

  const handleSightingTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = event.target.value;
      onChange({
        ...value,
        sightingType: next ? (next as SightingType) : undefined,
      });
    },
    [onChange, value]
  );

  const handleLocationChange = useCallback(
    (lat?: number, lon?: number, name?: string) => {
      if (lat === undefined || lon === undefined) {
        onChange({ ...value, location: undefined });
        return;
      }
      onChange({
        ...value,
        location: {
          lat,
          lon,
          locationName: name,
          radiusKm: location.radiusKm ?? DEFAULT_RADIUS_KM,
        },
      });
    },
    [onChange, value, location.radiusKm]
  );

  const handleRadiusDelta = useCallback(
    (delta: number) => {
      if (location.lat === undefined || location.lon === undefined) {
        return;
      }
      const current = location.radiusKm ?? DEFAULT_RADIUS_KM;
      const next = Math.min(MAX_RADIUS_KM, Math.max(MIN_RADIUS_KM, current + delta));
      if (next === current) return;
      onChange({
        ...value,
        location: {
          ...location,
          radiusKm: next,
        },
      });
    },
    [onChange, value, location]
  );

  const handleFromDateChange = useCallback(
    (next?: string) => {
      onChange({
        ...value,
        dateRange: {
          ...dateRange,
          fromDate: next,
        },
      });
    },
    [onChange, value, dateRange]
  );

  const handleToDateChange = useCallback(
    (next?: string) => {
      onChange({
        ...value,
        dateRange: {
          ...dateRange,
          toDate: next,
        },
      });
    },
    [onChange, value, dateRange]
  );

  const handleClearFilters = useCallback(() => {
    onChange({});
  }, [onChange]);

  return (
    <div className={clsx('search-filters', className)}>
      <div className="search-filters-toolbar">
        <button
          type="button"
          className="search-filters-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
          <span>{expanded ? t('search.hideFilters') : t('search.showFilters')}</span>
          {!expanded && hasActiveFilters && (
            <span className="search-filters-active-dot" aria-label={t('search.filtersActive')} />
          )}
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            className="search-filters-clear"
            onClick={handleClearFilters}
          >
            {t('search.clearFilters')}
          </button>
        )}
      </div>

      {expanded && (
        <div className="search-filters-panel">
          <div className="search-filter-row">
            <span className="search-filter-label">{t('filter.dateRange')}</span>
            <div className="search-filter-date-range">
              <DateTimePicker
                label={t('filter.from')}
                value={dateRange.fromDate}
                onChange={handleFromDateChange}
                clearButton="icon"
              />
              <DateTimePicker
                label={t('filter.to')}
                value={dateRange.toDate}
                onChange={handleToDateChange}
                clearButton="icon"
              />
            </div>
          </div>

          <div className="search-filter-row">
            <span className="search-filter-label">{t('filter.location')}</span>
            <div className="search-filter-location">
              <LocationPicker
                latitude={location.lat}
                longitude={location.lon}
                locationName={location.locationName}
                radiusKm={location.radiusKm ?? DEFAULT_RADIUS_KM}
                onLocationChange={handleLocationChange}
              />
              <div className={clsx('search-filter-radius', location.lat === undefined || location.lon === undefined ? 'search-filter-radius--disabled' : undefined)}>
                <span id="search-filter-radius-label" className="search-filter-radius-label">
                  {t('filter.radius')}
                </span>
                <div
                  className="search-filter-radius-control"
                  role="group"
                  aria-labelledby="search-filter-radius-label"
                >
                  <button
                    type="button"
                    className="search-filter-radius-btn"
                    onClick={() => handleRadiusDelta(-1)}
                    disabled={
                      location.lat === undefined ||
                      location.lon === undefined ||
                      (location.radiusKm ?? DEFAULT_RADIUS_KM) <= MIN_RADIUS_KM
                    }
                    aria-label={t('search.radiusDecrease')}
                  >
                    <span aria-hidden="true">−</span>
                  </button>
                  <output className="search-filter-radius-value" aria-live="polite">
                    {location.radiusKm ?? DEFAULT_RADIUS_KM}
                  </output>
                  <button
                    type="button"
                    className="search-filter-radius-btn"
                    onClick={() => handleRadiusDelta(1)}
                    disabled={
                      location.lat === undefined ||
                      location.lon === undefined ||
                      (location.radiusKm ?? DEFAULT_RADIUS_KM) >= MAX_RADIUS_KM
                    }
                    aria-label={t('search.radiusIncrease')}
                  >
                    <span aria-hidden="true">+</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="search-filter-row">
            <label className="search-filter-label" htmlFor="search-filter-sighting-type">
              {t('filter.sightingType')}
            </label>
            <select
              id="search-filter-sighting-type"
              className="search-filter-select"
              value={sightingType ?? ''}
              onChange={handleSightingTypeChange}
            >
              <option value="">{t('filter.any')}</option>
              {SIGHTING_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.emoji} {getLocalizedSightingTypeLabel(opt.value, sightingLanguage)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
